/*
 * Parlevi UI — module seam entry (Phase 4).
 *
 * SEAM CONTRACT
 * - Execution order: classic scripts parse-run first, then deferred modules (this file),
 *   then DOMContentLoaded → jQuery ready-closures. window.PL is therefore ALWAYS set
 *   before any ready-block or user interaction runs. Classic code must never call PL.*
 *   at parse time — only from ready-blocks or event handlers.
 * - No top-level await anywhere in the module graph: it would push module evaluation
 *   past DOMContentLoaded and break the guarantee above.
 * - Imports use BARE specifiers from the import map (templates/fragments/importmap.html),
 *   never relative paths — relative imports bypass the content-hash fingerprinting chain
 *   and serve stale code after edits.
 * - Components dispatch NATIVE CustomEvents; jQuery listeners must read
 *   e.originalEvent.detail (jQuery 1.9 does not copy `detail` onto its wrapper).
 *   The reverse direction is not supported: legacy code calls component APIs directly,
 *   never jQuery .trigger() at a component.
 * - Fail loud: migrated legacy call sites use window.PL with NO existence guards —
 *   a broken seam should throw visibly, not degrade silently.
 */

import { layers } from "pl/layers";
import { hover } from "pl/hover";
import { focus } from "pl/focus";
import { tip } from "pl/tip";
import { mode } from "pl/mode";
import { drag } from "pl/drag";
import { resize } from "pl/resize";
import { fly, flyFrame, shrinkInto } from "pl/fly";
import { icon } from "pl/icon";
import { choreo } from "pl/choreo";
import { input } from "pl/input";
import Sortable from "sortablejs";

if (window.PL) {
	console.error("PL: duplicate seam boot — a template loads the module entry twice");
}

window.PL = {
	version: "p4-6b",
	layers,   // dismiss/layer stack (capture-phase Esc + outside-pointerdown)
	hover,    // hover-intent machine (intent/grace timers, dwell)
	focus,    // remember/restore-to-opener
	tip,      // shared popup tip (hover tooltip + headed error variants)
	mode,     // guided/normal app MODE (body class + `guided` user attr + plmodechange)
	drag,     // pointer-based free dragging (jQuery UI .draggable replacement)
	resize,   // edge-handle panel resizing (jQuery UI .resizable replacement)
	fly,      // fly-to-list transfer frame (jQuery UI "transfer" effect replacement, D21)
	flyFrame, // …the same frame, PERSISTENT: one outline hopping from stop to stop
	shrinkInto, // "genie" close: source shrinks/fades into a target, rather than an outline filling it
	icon,     // inline SVG icons (the expand/shrink pair the tooltip and help menu share)
	choreo,   // shared beat tempo + panel unfold/fold (the sign-off and the shrink pill share it)
	input,    // input modality: input.touch, read off main.html's plTouch class (tablet support)
	Sortable, // vendored SortableJS 1.15.7 (MIT) — list reordering (jQuery UI .sortable replacement)
	// Key codes for the legacy e.which/e.keyCode handlers ($.ui.keyCode replacement;
	// only handler bodies read these, so the seam guarantee covers every use).
	keys: { ENTER: 13, ESCAPE: 27, SPACE: 32, UP: 38, DOWN: 40 }
	// Later steps: position (anchored placement).
};

// The touch layer (tablet-support-plan.md §2.3, seam T-3): a desktop never downloads, parses or
// runs js/touch/. NOT awaited — a top-level await would push module evaluation past
// DOMContentLoaded (contract above); the touch layer attaches its own listeners when it lands.
if (input.touch) {
	import("touch/index").catch(e => console.error("PL: the touch layer failed to load", e));
}
